import { Icon, Switch, Tooltip } from "@mui/material";
import MDBox from "components/MDBox";
import UiTypography from "components/base/UiTypography";
import { WorksheetCostCategory } from "generated/graphql";
import { draftColors } from "modules/proposals/draft/draftDesignTokens";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { WorksheetItemInput } from "DDD/action-objects/WorksheetItemUpdate";
import { WorksheetInputField, WorksheetSelectField } from "./WorksheetFormFields";

const QUANTITY_MODE_OPTIONS = [
  { value: "", label: "Material allocation" },
  { value: "manual", label: "Manual quantity" },
  { value: "product_tons", label: "Product tons" },
];

const CAPACITY_UNIT_OPTIONS = [
  { value: "Tons", label: "Tons" },
  { value: "Yards", label: "Yards" },
];

type WorksheetHaulingFieldsProps = {
  index: number;
  isReadOnly?: boolean;
};

export default function WorksheetHaulingFields({ index, isReadOnly }: WorksheetHaulingFieldsProps) {
  const { control, setValue } = useFormContext<WorksheetItemInput>();
  const values = useWatch({ control }) as WorksheetItemInput;
  const row = values.costRows?.[index];
  const mode = row?.haulQuantityMode ?? "";
  const materialRows = (values.costRows ?? []).filter(
    (candidate) => candidate.category === WorksheetCostCategory.MATERIAL
  );
  const materialOptions = materialRows.map((materialRow) => ({
    value: materialRow.rowUuid,
    label: materialRow.name || "Material",
  }));

  const handleRestore = () => {
    setValue(`costRows.${index}.quantityOverridden`, false, { shouldDirty: true });
  };

  return (
    <MDBox display="flex" flexDirection="column" gap="10px">
      <MDBox display="flex" alignItems="center" justifyContent="flex-end">
        <MDBox display="flex" alignItems="center" gap="8px">
          <UiTypography uiVariant="label-xs" sx={{ color: draftColors.textSecondary }}>
            Full loads only
          </UiTypography>
          <Controller
            control={control}
            name={`costRows.${index}.haulFullLoads`}
            render={({ field }) => (
              <Switch
                size="small"
                checked={field.value}
                disabled={isReadOnly}
                onChange={(event) => field.onChange(event.target.checked)}
              />
            )}
          />
          {row?.quantityOverridden && !isReadOnly && (
            <Tooltip title="Restore planned quantities">
              <Icon
                fontSize="small"
                sx={{ cursor: "pointer", color: draftColors.textAction }}
                onClick={handleRestore}
              >
                restart_alt
              </Icon>
            </Tooltip>
          )}
        </MDBox>
      </MDBox>

      <MDBox
        display="grid"
        gridTemplateColumns="repeat(2, minmax(0, 1fr))"
        gap="8px"
        alignItems="start"
      >
        <WorksheetSelectField
          name={`costRows.${index}.haulQuantityMode`}
          label="Haul Quantity"
          options={QUANTITY_MODE_OPTIONS}
          isReadOnly={isReadOnly}
        />
        {mode === "" && (
          <>
            <WorksheetSelectField
              name={`costRows.${index}.haulMaterialRowUuid`}
              label="Material Row"
              options={materialOptions}
              isReadOnly={isReadOnly}
            />
            <WorksheetInputField
              name={`costRows.${index}.haulAllocationPercent`}
              label="Allocation %"
              placeholder="0"
              isReadOnly={isReadOnly}
            />
          </>
        )}
        {mode === "manual" && (
          <>
            <WorksheetInputField
              name={`costRows.${index}.haulQuantity`}
              label="Quantity"
              placeholder="0"
              isReadOnly={isReadOnly}
            />
            <WorksheetSelectField
              name={`costRows.${index}.haulUnit`}
              label="Unit"
              options={CAPACITY_UNIT_OPTIONS}
              isReadOnly={isReadOnly}
            />
          </>
        )}
        <WorksheetInputField
          name={`costRows.${index}.truckCapacity`}
          label="Capacity"
          placeholder="0"
          isReadOnly={isReadOnly}
        />
        <WorksheetSelectField
          name={`costRows.${index}.haulCapacityUnit`}
          label="Capacity Unit"
          options={CAPACITY_UNIT_OPTIONS}
          isReadOnly={isReadOnly}
        />
      </MDBox>

      <MDBox
        display="grid"
        gridTemplateColumns="repeat(2, minmax(0, 1fr))"
        gap="8px"
        alignItems="start"
      >
        <WorksheetInputField
          name={`costRows.${index}.haulRoundTripHours`}
          label="Round Trip"
          placeholder="0"
          isReadOnly={isReadOnly}
        />
        <WorksheetInputField
          name={`costRows.${index}.haulLoadHours`}
          label="Load Hrs"
          placeholder="0"
          isReadOnly={isReadOnly}
        />
        <WorksheetInputField
          name={`costRows.${index}.haulUnloadHours`}
          label="Unload Hrs"
          placeholder="0"
          isReadOnly={isReadOnly}
        />
        <WorksheetInputField
          name={`costRows.${index}.haulPlanningHours`}
          label="Job Time"
          placeholder="From production"
          isReadOnly={isReadOnly}
        />
        <WorksheetInputField
          name={`costRows.${index}.haulMaxTripsPerDay`}
          label="Max Trips/Day"
          placeholder="—"
          isReadOnly={isReadOnly}
        />
      </MDBox>
    </MDBox>
  );
}
